/**
 * Worker API client.
 *
 * In dev, requests hit `/api/...` and Vite proxies to the local Worker.
 * In prod, set VITE_API_BASE_URL to the deployed Worker URL. The fallback
 * keeps the live Pages app functional if that env var is missing.
 */
import type {
  ApiErrorBody,
  Client,
  Lead,
  LeadInput,
  ManagedTableKey,
  ManagedTableListResponse,
  ManagedTableRecordResponse,
  PaginatedResponse,
  Proposal,
  Task,
  TimeEntry,
  User,
  VaultDocument,
  VaultDocumentDetail,
  VaultDocumentInput,
  VaultDocumentLink,
  VaultDocumentPatch,
  VaultDocumentSearchParams,
  VaultDocumentSearchResponse,
  VaultDocumentUpsertRequest,
  VaultDocumentUpsertResponse,
  VaultDocumentVersion,
  VaultLinkInput,
  VaultListParams,
  Website,
} from '@oneup/shared';
import type { AliceRegistryResponse } from '@command-center/core-platform';
import { authedFetch } from '@/auth/supabase-browser';

const FALLBACK_PROD_API_BASE_URL = 'https://oneup-admin-portal.dakota-276.workers.dev';

const configuredBaseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export const API_BASE_URL =
  configuredBaseUrl || (import.meta.env.PROD ? FALLBACK_PROD_API_BASE_URL : '');

export function getApiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  constructor(
    status: number,
    body: ApiErrorBody | { error?: { message?: string; code?: string } },
  ) {
    const message = body.error?.message ?? `HTTP ${status}`;
    super(message);
    this.status = status;
    this.code = body.error?.code ?? 'unknown_error';
    if ('details' in (body.error ?? {})) {
      this.details = (body as ApiErrorBody).error.details;
    }
  }
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = getApiUrl(path);
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // Identify the actor for activity logs in dev. In prod, Cloudflare Access
  // sets Cf-Access-Jwt-Assertion automatically and the Worker reads identity
  // from there.
  if (import.meta.env.DEV && !headers.has('X-Actor-Name')) {
    headers.set('X-Actor-Name', 'Dakota');
  }

  const res = await fetchWithRateLimitRetry(url, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      // not JSON — keep undefined
    }
    throw new ApiClientError(
      res.status,
      body ?? { error: { code: 'http_error', message: res.statusText } },
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function fetchWithRateLimitRetry(url: string, init: RequestInit): Promise<Response> {
  const first = await authedFetch(url, init);
  if (first.status !== 429) return first;

  const retryAfter = parseRetryAfter(first.headers.get('Retry-After'));
  if (retryAfter === undefined) return first;

  await new Promise((resolve) => setTimeout(resolve, retryAfter));
  return authedFetch(url, init);
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;

  const retryAt = Date.parse(value);
  if (!Number.isNaN(retryAt)) return Math.max(0, retryAt - Date.now());
  return undefined;
}

// ---------- Tasks ----------

export interface ListTasksParams {
  status?: string;
  clientId?: string;
  websiteId?: string;
  assigneeEmail?: string;
  search?: string;
  includeClosed?: boolean;
  limit?: number;
}

export const tasksApi = {
  list: (params: ListTasksParams = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.clientId) qs.set('clientId', params.clientId);
    if (params.websiteId) qs.set('websiteId', params.websiteId);
    if (params.assigneeEmail) qs.set('assigneeEmail', params.assigneeEmail);
    if (params.search) qs.set('search', params.search);
    if (params.includeClosed === false) qs.set('includeClosed', 'false');
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<PaginatedResponse<Task>>(`/api/tasks${suffix}`);
  },
  get: (id: string) => request<Task>(`/api/tasks/${id}`),
  patch: (
    id: string,
    patch: Partial<Task> & {
      clientId?: string | null;
      websiteId?: string | null;
      parentTaskId?: string | null;
    },
  ) =>
    request<Task>(`/api/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  delete: (id: string) => request<{ ok: true }>(`/api/tasks/${id}`, { method: 'DELETE' }),
  create: (input: {
    name: string;
    notes?: string;
    description?: string;
    status?: string;
    clientId?: string;
    websiteId?: string;
    parentTaskId?: string;
    priority?: string;
    dueDate?: string;
    billable?: boolean;
    source?: string;
    type?: string;
  }) =>
    request<Task>(`/api/tasks`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /**
   * Legacy one-shot Send Update: posts a Make Orchestrator event with task data, no AI
   * draft, no review step. Kept for external/backwards compatibility only.
   * Admin Portal UI uses aiSendUpdateApi.draft + send so every send is reviewed.
   */
  sendUpdate: (
    id: string,
    input: { summary?: string; questions?: string; fromEmail?: string; toEmails?: string[] } = {},
  ) =>
    request<{ ok: true; payload: Record<string, unknown> }>(`/api/tasks/${id}/send-update`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ---------- AI: Quick Capture ----------

export interface AiSuggestedFieldUpdates {
  status?: string | null;
  priority?: string | null;
  nextAction?: string | null;
  definitionOfDone?: string | null;
}

export interface AiQuickCaptureProcessResponse {
  /** ai_logs row id, or null if Supabase is not configured. */
  logId: string | null;
  /** The notes that were auto-appended to Task Notes (already persisted). */
  notesAppended: string;
  suggested: {
    fieldUpdates: AiSuggestedFieldUpdates;
    newQuestions: string[];
  };
  reasoning: string;
  /** Non-fatal warnings — e.g. vision_failed when a file URL couldn't be read. */
  warnings: string[];
}

export interface AiQuickCaptureApplyResponse {
  ok: true;
  task: Task;
  applied: {
    fieldUpdates: Partial<Task>;
    questionsAppended: string[];
  };
  warnings: string[];
}

export const aiQuickCaptureApi = {
  process: (
    taskId: string,
    input: { text?: string; voiceTranscript?: string; fileUrls?: string[] },
  ) =>
    request<AiQuickCaptureProcessResponse>(`/api/tasks/${taskId}/quick-capture/process`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  apply: (
    taskId: string,
    input: {
      logId?: string | null;
      fieldUpdates?: AiSuggestedFieldUpdates;
      newQuestions?: string[];
    },
  ) =>
    request<AiQuickCaptureApplyResponse>(`/api/tasks/${taskId}/quick-capture/apply`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  discard: (taskId: string, logId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/quick-capture/discard`, {
      method: 'POST',
      body: JSON.stringify({ logId }),
    }),
};

// ---------- AI: Send Update ----------

export interface AiSendUpdateDraftResponse {
  logId: string | null;
  subject: string;
  body: string;
  questions: string[];
  sendDefaults: {
    fromOptions: string[];
    fromEmail: string;
    senderSource: 'gmail' | 'fallback';
    connectedEmail?: string;
    toEmails: string[];
    recipientOptions: Array<{
      name: string;
      email: string;
      isDefault?: boolean;
    }>;
  };
}

export const aiSendUpdateApi = {
  draft: (taskId: string) =>
    request<AiSendUpdateDraftResponse>(`/api/tasks/${taskId}/send-update/draft`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  send: (
    taskId: string,
    input: {
      logId?: string | null;
      subject: string;
      body: string;
      questions?: string[];
      fromEmail?: string;
      toEmails?: string[];
      deliveryMode?: 'auto' | 'gmail' | 'make';
    },
  ) =>
    request<{ ok: true; payload: Record<string, unknown> }>(
      `/api/tasks/${taskId}/send-update/send`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  cancel: (taskId: string, logId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}/send-update/cancel`, {
      method: 'POST',
      body: JSON.stringify({ logId }),
    }),
};

// ---------- AI: Per-task activity timeline ----------

export interface AiLogRow {
  id: string;
  created_at: string;
  updated_at: string;
  task_id: string | null;
  client_id: string | null;
  action_type: 'quick_capture' | 'send_update' | 'chat' | 'recap' | 'triage' | 'summarize';
  status: 'pending_confirmation' | 'pending_send' | 'success' | 'sent' | 'discarded' | 'error';
  user_input: unknown;
  ai_input: unknown;
  ai_output: unknown;
  applied_changes: unknown;
  error_message: string | null;
  model: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  cost_cents: number | null;
  /** Compact audit summary (workflow, tools called, duration). Best-effort. */
  metadata?: Record<string, unknown> | null;
}

export const aiLogsApi = {
  listForTask: (taskId: string, limit = 20) =>
    request<{ items: AiLogRow[]; total: number }>(`/api/tasks/${taskId}/ai-logs?limit=${limit}`),
  list: (limit = 100) =>
    request<{ items: AiLogRow[]; total: number }>(`/api/ai-logs?limit=${limit}`),
};

// ---------- Copilot v0 (Pass 4) ----------
//
// Worker route: POST /api/copilot/chat (Track 1)
//                POST /api/tasks/:id/recap/draft (Track 3)
//                GET  /api/copilot/usage (Track 4)

export interface CopilotMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | null;
  tool_calls?: Array<{
    id: string;
    type: 'function';
    function: { name: string; arguments: string };
  }>;
  tool_call_id?: string;
  name?: string;
}

export interface CopilotPendingConfirmation {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  summary: string;
  rationale?: string;
}

export interface CopilotEntityMention {
  type: 'organization';
  id: string;
  name: string;
  handle?: string;
  voiceCode?: string;
}

export interface CopilotSelectedClientContext {
  id: string;
  name: string;
  handle?: string;
  voiceCode?: string;
  aliases?: string[];
  status?: string;
  website?: string;
  primaryContact?: string;
  billingEmail?: string;
  stripeCustomerId?: string;
  vapiAssistantId?: string;
}

export interface CopilotChatResponse {
  messages: CopilotMessage[];
  usage: { tokensInput: number; tokensOutput: number; costCents: number };
  pendingConfirmation: CopilotPendingConfirmation | null;
  logId: string | null;
  warnings: string[];
  meta: {
    aiCallsMade: number;
    toolCallsExecuted: number;
    durationMs: number;
  };
}

export interface CopilotRecapResponse {
  logId: string | null;
  recap: string;
  summary: string;
  usage: {
    tokensInput: number | null;
    outputTokens?: number | null;
    tokensOutput: number | null;
    costCents: number | null;
  } | null;
}

export interface CopilotUsageResponse {
  today: {
    totalCents: number;
    copilotOnlyCents: number;
    byActionType: Record<string, number>;
  };
  budget: {
    dailyCapCents: number | null;
    remainingCents: number | null;
    copilotActionTypes: ReadonlyArray<string>;
  };
  note?: string;
}

export const copilotApi = {
  /**
   * Send a chat message (or continue a tool-loop conversation). The Worker
   * runs the tool loop server-side and returns the full conversation plus
   * a `pendingConfirmation` envelope when the model wants to write.
   *
   * Continuing after a write confirmation:
   *   1. Receive `pendingConfirmation` from a prior call.
   *   2. Render <AiConfirmCard />; on accept, call the matching write
   *      endpoint (e.g. tasksApi.patch(id, patch)).
   *   3. POST back to copilotApi.chat with all prior messages PLUS one
   *      additional `{ role: 'tool', tool_call_id, content: '<result>' }`
   *      message corresponding to pendingConfirmation.toolCallId.
   */
  chat: (input: {
    messages: CopilotMessage[];
    taskContextId?: string;
    proposalContextId?: string;
    /** Optional OpenAI model id; the Worker validates it against an allowlist. */
    model?: string;
    /** The record the operator is currently viewing, for grounding. */
    contextRecord?: { type: string; id?: string; label?: string };
    /** Connector ids switched off in Alice settings (their tools are withheld). */
    disabledConnectors?: string[];
    /** Per-turn image data URLs for Alice vision (not persisted). */
    imageUrls?: string[];
    /** Structured entity mentions selected in the chat UI; do not infer from raw text. */
    mentions?: CopilotEntityMention[];
    /** Extra per-turn context derived from UI controls such as client mention chips. */
    context?: {
      selectedClient?: CopilotSelectedClientContext;
    };
  }) =>
    request<CopilotChatResponse>(`/api/copilot/chat`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /**
   * Generate a closeout recap for a task. Returns markdown body + 1-line
   * summary. Does NOT touch Airtable; the frontend drops the draft into
   * the notes editor and Dakota saves manually.
   */
  draftRecap: (taskId: string) =>
    request<CopilotRecapResponse>(`/api/tasks/${taskId}/recap/draft`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  /**
   * Today's spend across all AI surfaces, with budget headroom. Used by the
   * Settings cost card and (eventually) by the palette to surface a warning
   * when the budget is nearly exhausted.
   */
  usage: () => request<CopilotUsageResponse>(`/api/copilot/usage`),
  /** Read-only Alice Tooling & Workflow Registry catalog (tools/connections/prompts). */
  registry: () => request<AliceRegistryResponse>(`/api/copilot/registry`),
};

// ---------- Clients / Users / Websites ----------

export interface ListClientsParams {
  status?: string;
  q?: string;
}

export interface ListUsersParams {
  status?: string;
  type?: string;
  clientId?: string;
  q?: string;
}

export interface ListWebsitesParams {
  status?: string;
  clientId?: string;
  q?: string;
}

export interface ListProposalsParams {
  status?: string;
  visibility?: string;
  filter?: string;
  search?: string;
  organizationId?: string;
  recordIds?: string[];
  limit?: number;
}

export interface ListLeadsParams {
  status?: string;
  search?: string;
  q?: string;
  limit?: number;
}

function withQuery(path: string, params: Array<[string, string | undefined]>) {
  const qs = new URLSearchParams();
  params.forEach(([key, value]) => {
    if (value) qs.set(key, value);
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return `${path}${suffix}`;
}

export const managedTablesApi = {
  list: (
    key: ManagedTableKey,
    params: {
      status?: string;
      search?: string;
      q?: string;
      limit?: number;
      recordIds?: string[];
    } = {},
  ) =>
    request<ManagedTableListResponse>(
      withQuery(`/api/managed-tables/${key}`, [
        ['status', params.status],
        ['search', params.search ?? params.q],
        ['limit', params.limit ? String(params.limit) : undefined],
        ['recordIds', params.recordIds?.length ? params.recordIds.join(',') : undefined],
      ]),
    ),
  get: (key: ManagedTableKey, recordId: string) =>
    request<ManagedTableRecordResponse>(
      `/api/managed-tables/${key}/${encodeURIComponent(recordId)}`,
    ),
  patch: (key: ManagedTableKey, recordId: string, fields: Record<string, unknown>) =>
    request<ManagedTableRecordResponse>(
      `/api/managed-tables/${key}/${encodeURIComponent(recordId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ fields }),
      },
    ),
  delete: (key: ManagedTableKey, recordId: string) =>
    request<{ ok: true; id: string }>(
      `/api/managed-tables/${key}/${encodeURIComponent(recordId)}`,
      { method: 'DELETE' },
    ),
};

export const clientsApi = {
  list: (params: ListClientsParams = {}) =>
    request<PaginatedResponse<Client>>(
      withQuery('/api/clients', [
        ['status', params.status],
        ['q', params.q],
      ]),
    ),
  get: (id: string) => request<Client>(`/api/clients/${id}`),
  create: (input: Partial<Client> & { name: string }) =>
    request<Client>('/api/clients', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<Client>) =>
    request<Client>(`/api/clients/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  delete: (id: string) => request<{ ok: true }>(`/api/clients/${id}`, { method: 'DELETE' }),
};

export const usersApi = {
  list: (params: ListUsersParams = {}) =>
    request<PaginatedResponse<User>>(
      withQuery('/api/users', [
        ['status', params.status],
        ['type', params.type],
        ['clientId', params.clientId],
        ['q', params.q],
      ]),
    ),
  get: (id: string) => request<User>(`/api/users/${id}`),
  create: (input: Partial<User> & { fullName: string }) =>
    request<User>('/api/users', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<User>) =>
    request<User>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  delete: (id: string) => request<{ ok: true }>(`/api/users/${id}`, { method: 'DELETE' }),
};

export const websitesApi = {
  list: (params: ListWebsitesParams = {}) =>
    request<PaginatedResponse<Website>>(
      withQuery('/api/websites', [
        ['status', params.status],
        ['clientId', params.clientId],
        ['q', params.q],
      ]),
    ),
  get: (id: string) => request<Website>(`/api/websites/${id}`),
  create: (input: Partial<Website> & { url: string }) =>
    request<Website>('/api/websites', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<Website>) =>
    request<Website>(`/api/websites/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  delete: (id: string) => request<{ ok: true }>(`/api/websites/${id}`, { method: 'DELETE' }),
};

export const proposalsApi = {
  list: (params: ListProposalsParams = {}) =>
    request<PaginatedResponse<Proposal>>(
      withQuery('/api/proposals', [
        ['status', params.status],
        ['visibility', params.visibility],
        ['filter', params.filter],
        ['search', params.search],
        ['organizationId', params.organizationId],
        ['recordIds', params.recordIds?.length ? params.recordIds.join(',') : undefined],
        ['limit', params.limit ? String(params.limit) : undefined],
      ]),
    ),
  get: (id: string) => request<Proposal>(`/api/proposals/${id}`),
  create: (input: Partial<Proposal> & { proposalName: string }) =>
    request<Proposal>('/api/proposals', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<Proposal>) =>
    request<Proposal>(`/api/proposals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
};

export const leadsApi = {
  list: (params: ListLeadsParams = {}) =>
    request<PaginatedResponse<Lead>>(
      withQuery('/api/leads', [
        ['status', params.status],
        ['search', params.search ?? params.q],
        ['limit', params.limit ? String(params.limit) : undefined],
      ]),
    ),
  get: (id: string) => request<Lead>(`/api/leads/${id}`),
  create: (input: LeadInput & { projectName: string }) =>
    request<Lead>('/api/leads', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, patch: LeadInput) =>
    request<Lead>(`/api/leads/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  delete: (id: string) => request<{ ok: true }>(`/api/leads/${id}`, { method: 'DELETE' }),
};

// ---------- Time entries ----------

export const timeEntriesApi = {
  listByTask: (taskId: string) =>
    request<PaginatedResponse<TimeEntry>>(`/api/time-entries?taskId=${encodeURIComponent(taskId)}`),
  create: (input: {
    taskId: string;
    taskName?: string;
    notes?: string;
    billable: boolean;
    durationMinutes: number;
    startedAt: string;
    endedAt: string;
    clientId?: string;
  }) =>
    request<TimeEntry>(`/api/time-entries`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ---------- Communications ----------

export type CommunicationTimelineSource = 'gmail' | 'quick_note' | 'activity';

export interface GmailThreadSummary {
  id: string;
  historyId?: string;
  subject: string;
  snippet?: string;
  messageIds: string[];
  participants: Array<{
    name?: string;
    email: string;
    role: 'from' | 'to' | 'cc';
  }>;
  fromEmail?: string;
  toEmails: string[];
  ccEmails: string[];
  startedAt?: string;
  lastMessageAt?: string;
  messageCount: number;
  labelIds: string[];
  gmailUrl: string;
  summaryMarkdown: string;
  match?: {
    email?: string;
    userId?: string;
    userName?: string;
    organizationId?: string;
    organizationName?: string;
    matchedEmails: string[];
  };
}

export interface GmailThreadMessage {
  id: string;
  threadId: string;
  subject: string;
  from?: {
    name?: string;
    email: string;
    role: 'from';
  };
  to: Array<{
    name?: string;
    email: string;
    role: 'to';
  }>;
  cc: Array<{
    name?: string;
    email: string;
    role: 'cc';
  }>;
  date?: string;
  messageId?: string;
  references?: string;
  inReplyTo?: string;
  snippet?: string;
  bodyText: string;
}

export interface GmailThreadMessages {
  id: string;
  historyId?: string;
  subject: string;
  messages: GmailThreadMessage[];
}

export interface GmailSendAsAlias {
  email: string;
  displayName?: string;
  isDefault?: boolean;
  verificationStatus?: string;
}

export interface GmailSendAsResponse {
  aliases: GmailSendAsAlias[];
  connected: boolean;
  connectedEmail?: string;
}

export interface GmailSyncLimits {
  enabled: boolean;
  maxThreads: number;
  maxGmailRequests: number;
  maxGmailQuotaUnits: number;
  dailyQuotaUnits: number;
  maxRuntimeMs: number;
  lockTtlSeconds: number;
  matchAirtable: boolean;
  maxWrites: number;
}

export interface GmailSyncStatus {
  id: string;
  status: string;
  startedAt: string;
  finishedAt?: string;
  labelName?: string;
  threadsFound: number;
  threadsCreated: number;
  threadsUpdated: number;
  threadsSkipped: number;
  error?: string;
  elapsedMs?: number;
  gmailRequests?: number;
  gmailQuotaUnits?: number;
  supabaseWrites?: number;
}

export interface GmailIntegrationStatus {
  connected: boolean;
  connectedEmail?: string;
  reconnectRequired: boolean;
  connectionId?: string;
  actorEmail?: string;
  actorProvider?: string;
  connectedAt?: string;
  lastUsedAt?: string;
  syncEnabled: boolean;
  limits: GmailSyncLimits;
  lastSync?: GmailSyncStatus;
  error?: {
    code: string;
    message: string;
  };
}

export interface GmailSyncTaggedResponse {
  labelName: string;
  labelId: string;
  imported: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  syncRunId: string | null;
  status: string;
  elapsedMs: number;
  gmailRequests: number;
  gmailQuotaUnits: number;
  supabaseWrites: number;
  limits: GmailSyncLimits;
  items: CommunicationTimelineItem[];
}

export interface CommunicationTimelineItem {
  id: string;
  source: CommunicationTimelineSource;
  sourceLabel: string;
  recordId?: string;
  title: string;
  summary?: string;
  occurredAt: string;
  status?: string;
  visibility?: string;
  clientId?: string;
  clientName?: string;
  userId?: string;
  userName?: string;
  contactEmail?: string;
  taskId?: string;
  websiteId?: string;
  href?: string;
  tags: string[];
  metadata: Record<string, unknown>;
}

export interface CommunicationRecordDetails {
  record: Record<string, unknown>;
  events: Array<Record<string, unknown>>;
  matches: Array<Record<string, unknown>>;
  draftTasks: Array<Record<string, unknown>>;
}

export interface CommunicationLinkPatch {
  clientId?: string | null;
  userId?: string | null;
  taskId?: string | null;
  websiteId?: string | null;
}

export interface GmailSendInput {
  fromEmail: string;
  toEmails: string[];
  ccEmails?: string[];
  subject: string;
  bodyMarkdown: string;
  clientId?: string;
  userId?: string;
  taskId?: string;
  websiteId?: string;
  replyToRecordId?: string;
  threadId?: string;
}

export interface GmailDraftInput {
  instruction: string;
  clientName?: string;
  contactEmails?: string[];
  taskName?: string;
  threadSubject?: string;
}

export interface GmailDraftResult {
  subject: string;
  bodyMarkdown: string;
  toEmails: string[];
}

export interface CommunicationsListParams {
  clientId?: string;
  userId?: string;
  taskId?: string;
  websiteId?: string;
  source?: 'all' | CommunicationTimelineSource;
  search?: string;
  limit?: number;
}

export const communicationsApi = {
  list: (params: CommunicationsListParams = {}) =>
    request<{ items: CommunicationTimelineItem[]; total: number; sources: string[] }>(
      withQuery('/api/communications', [
        ['clientId', params.clientId],
        ['userId', params.userId],
        ['taskId', params.taskId],
        ['websiteId', params.websiteId],
        ['source', params.source],
        ['search', params.search],
        ['limit', params.limit ? String(params.limit) : undefined],
      ]),
    ),
  get: (id: string) => request<CommunicationRecordDetails>(`/api/communications/${id}`),
  getThread: (id: string) => request<GmailThreadMessages>(`/api/communications/${id}/thread`),
  gmailStatus: () => request<GmailIntegrationStatus>('/api/communications/gmail/status'),
  syncTaggedGmail: (input: { label?: string; search?: string; limit?: number } = {}) =>
    request<GmailSyncTaggedResponse>('/api/communications/gmail/sync-tagged', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  taggedGmail: (params: { label?: string; search?: string; limit?: number } = {}) =>
    request<{ labelName: string; labelId: string; items: GmailThreadSummary[] }>(
      withQuery('/api/communications/gmail/tagged', [
        ['label', params.label],
        ['search', params.search],
        ['limit', params.limit ? String(params.limit) : undefined],
      ]),
    ),
  linkGmailThread: (input: {
    gmailThreadId: string;
    taskId?: string;
    clientId?: string;
    userId?: string;
    summary?: string;
    labelName?: string;
  }) =>
    request<{ item: CommunicationTimelineItem; created: boolean; thread: GmailThreadSummary }>(
      '/api/communications/gmail/link',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),
  patchLinks: (id: string, input: CommunicationLinkPatch) =>
    request<{ item: CommunicationTimelineItem; record: Record<string, unknown> }>(
      `/api/communications/${id}/links`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      },
    ),
  sendAs: () => request<GmailSendAsResponse>('/api/communications/gmail/send-as'),
  sendGmail: (input: GmailSendInput) =>
    request<{
      message: { id: string; threadId: string };
      item: CommunicationTimelineItem;
    }>('/api/communications/gmail/send', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  draftGmail: (input: GmailDraftInput) =>
    request<GmailDraftResult>('/api/communications/gmail/draft', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ---------- .MD Vault ----------

export const vaultApi = {
  list: (params: VaultListParams = {}) =>
    request<{ items: VaultDocument[]; total: number }>(
      withQuery('/api/vault/documents', [
        ['q', params.q],
        ['docType', params.docType],
        ['status', params.status],
        ['visibility', params.visibility],
        ['folderPath', params.folderPath],
        ['limit', params.limit ? String(params.limit) : undefined],
      ]),
    ),
  get: (id: string) => request<VaultDocumentDetail>(`/api/vault/documents/${id}`),
  search: (params: VaultDocumentSearchParams = {}) =>
    request<VaultDocumentSearchResponse>(
      withQuery('/api/vault/documents/search', [
        ['q', params.q],
        ['tenantId', params.tenantId],
        ['docType', params.docType],
        ['status', params.status],
        ['visibility', params.visibility],
        ['limit', params.limit ? String(params.limit) : undefined],
      ]),
    ),
  create: (input: VaultDocumentInput) =>
    request<VaultDocumentDetail>('/api/vault/documents', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  upsert: (input: VaultDocumentUpsertRequest) =>
    request<VaultDocumentUpsertResponse>('/api/vault/documents/upsert', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (id: string, patch: VaultDocumentPatch) =>
    request<VaultDocumentDetail>(`/api/vault/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  archive: (id: string) =>
    request<VaultDocumentDetail>(`/api/vault/documents/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  delete: (id: string) =>
    request<{ ok: true; id: string }>(`/api/vault/documents/${id}`, {
      method: 'DELETE',
    }),
  publish: (id: string) =>
    request<VaultDocumentDetail>(`/api/vault/documents/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  addLink: (id: string, input: VaultLinkInput) =>
    request<VaultDocumentLink>(`/api/vault/documents/${id}/links`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  removeLink: (id: string, linkId: string) =>
    request<{ ok: true; id: string }>(`/api/vault/documents/${id}/links/${linkId}`, {
      method: 'DELETE',
    }),
  listForEntity: (entityType: string, entityId: string) =>
    request<{ items: VaultDocument[]; total: number }>(
      `/api/vault/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/documents`,
    ),
  versions: (id: string) =>
    request<{ items: VaultDocumentVersion[]; total: number }>(
      `/api/vault/documents/${id}/versions`,
    ),
  restoreVersion: (id: string, versionId: string) =>
    request<VaultDocumentDetail>(`/api/vault/documents/${id}/versions/${versionId}/restore`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

// ---------- Activity ----------

export interface ActivityRow {
  id: string;
  createdAt: string;
  actorName: string;
  entityType: string;
  entityId?: string;
  entityLabel?: string;
  action: string;
  oldValue?: unknown;
  newValue?: unknown;
  notes?: string;
  source?: string;
}

export const activityApi = {
  list: (params: { entityType?: string; entityId?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.entityType) qs.set('entityType', params.entityType);
    if (params.entityId) qs.set('entityId', params.entityId);
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ items: ActivityRow[]; total: number; note?: string }>(
      `/api/activity${suffix}`,
    );
  },
  log: (input: Partial<ActivityRow> & { entityType: string; action: string }) =>
    request<ActivityRow>(`/api/activity`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ---------- Schema ----------

export const schemaApi = {
  statusOptions: (table = 'ENT_Projects', field = 'Status') =>
    request<{
      table: string;
      field: string;
      type: string;
      options: Array<{ name: string; color?: string }>;
    }>(
      `/api/schema/status-options?table=${encodeURIComponent(table)}&field=${encodeURIComponent(field)}`,
    ),
};
